import Axios from 'axios';
import { STORAGE_KEYS, DEFAULT_LOCALE } from '@/shared/constants/app';
import { useAuthStore } from '@/core/stores/auth.store';
import { useAppStore } from '@/core/stores/app.store';
import { ToastService } from '@/shared/utilities/feedback/toast-service';
import storageService from '@/shared/services/storage.service';
import environmentService from '@/shared/services/environment.service';
import ApplicationError from '@/shared/services/core/application-error';
import { APP_CODES } from '@/shared/constants/authentication';
import { PATIENT_AUTH_ENTRY_PATH } from '@/core/config/authentication';
import { HTTP_STATUS, REQUEST_TIMEOUT_MS, mergeAxiosConfig } from './rest-client.config';

const restClientInstances = new Set();
let expiredSessionHandling = null;

async function endExpiredSession(message, { accessDisabled = false } = {}) {
  const auth = useAuthStore();
  if (!accessDisabled && !expiredSessionHandling && !auth.isLoggedIn) {
    return;
  }

  if (!expiredSessionHandling) {
    expiredSessionHandling = (async () => {
      const clearing = auth.signOut();
      const authStateVersion = auth.authStateVersion;
      try {
        await clearing;
      } catch {
        // Local authorization is already revoked; persistence must not block leaving the page.
        console.error('Failed to persist expired-session cleanup');
      }

      if (authStateVersion !== auth.authStateVersion) {
        return;
      }
      if (!accessDisabled) {
        ToastService.error(
          message ||
            ToastService.t(
              'toast.restClient.sessionExpired',
              'Your session has expired. Please log in again.',
            ),
        );
      }

      // Lazy loading avoids the router -> view -> API client import cycle.
      const { default: router } = await import('@/core/router');
      if (authStateVersion !== auth.authStateVersion) {
        return;
      }

      await router.replace(
        accessDisabled
          ? { path: '/auth/access-unavailable', state: { accessMessage: message || '' } }
          : PATIENT_AUTH_ENTRY_PATH,
      );
    })();
  }

  const handling = expiredSessionHandling;
  try {
    await handling;
  } catch {
    console.error('Failed to end the expired session');
  } finally {
    if (expiredSessionHandling === handling) {
      expiredSessionHandling = null;
    }
  }
}

export default class RestClient {
  #client;
  #version;
  #servicePath;
  #baseURL;

  constructor(servicePath, version = 'v1', baseURL = null) {
    if (!servicePath) {
      throw new Error('servicePath is required');
    }
    this.#version = version;
    const normalizedPath = servicePath.startsWith('/') ? servicePath : `/${servicePath}`;
    this.#servicePath = normalizedPath === '/' ? '' : normalizedPath;

    const apiUrlFromEnv = baseURL || environmentService.getApiUrlSync();

    const apiURL = apiUrlFromEnv ? apiUrlFromEnv.replace(/\/+$/, '') : '';

    this.#baseURL = apiURL;
    this.#client = this.createAxiosClient();

    restClientInstances.add(this);
  }

  static updateAllBaseURLs(newBaseURL) {
    restClientInstances.forEach((instance) => {
      instance.updateBaseURL(newBaseURL);
    });
  }

  updateBaseURL(newBaseURL) {
    if (!newBaseURL) {
      throw new Error('Base URL is required');
    }

    const apiURL = newBaseURL.replace(/\/+$/, '');

    this.#baseURL = apiURL;
    this.#client.defaults.baseURL = this.#baseURL;
  }

  createAxiosClient() {
    if (!this.#baseURL) {
      throw new Error('API_URL environment variable is not set');
    }

    const axiosInstance = Axios.create({
      baseURL: this.#baseURL,
      timeout: REQUEST_TIMEOUT_MS,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      withCredentials: false,
    });

    axiosInstance.interceptors.request.use(this.onRequest.bind(this));

    return axiosInstance;
  }

  async onRequest(config) {
    // Preserve an explicitly supplied session token, such as logout cleanup's token.
    const token = config.publicAuth || config.headers?.Authorization ? null : useAuthStore().token;

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const storedLang = await storageService.getItem(STORAGE_KEYS.LANGUAGE_KEY);
    const lang = (storedLang || DEFAULT_LOCALE).toLowerCase();
    const existingParams = config.params || {};
    if (!Object.prototype.hasOwnProperty.call(existingParams, 'lang')) {
      config.params = { ...existingParams, lang };
    }

    // Native storage reads can finish after a patient or environment switch.
    if (
      config.authStateVersion !== null &&
      config.authStateVersion !== useAuthStore().authStateVersion
    ) {
      throw new Axios.CanceledError();
    }

    return config;
  }

  async handleApplicationError(result, { silent = false, publicAuth = false, requestToken } = {}) {
    if (!publicAuth && result.code === APP_CODES.ONBOARDING_REQUIRED) {
      const { redirectToOnboarding } = await import('@/core/router');
      await redirectToOnboarding(result.payload?.status, requestToken);
      throw new Axios.CanceledError();
    }
    if (!publicAuth && result.code === APP_CODES.ACCOUNT_ACCESS_DISABLED) {
      await endExpiredSession(result.message, { accessDisabled: true });
      return;
    }
    if (!publicAuth && result.code === APP_CODES.UNAUTHORIZED) {
      await endExpiredSession(result.message);
      return;
    }
    if (!silent && result.code !== APP_CODES.SUCCESS && result.message) {
      ToastService.error(result.message);
    }
  }

  handleHttpError(err) {
    if (!Axios.isAxiosError(err)) {
      return;
    }

    if (err.response) {
      const { status, data } = err.response;
      if (data?.message) {
        ToastService.error(data.message);
      } else {
        switch (status) {
          case HTTP_STATUS.UNAUTHORIZED:
            ToastService.error(
              ToastService.t(
                'toast.restClient.httpUnauthorized',
                'Please sign in again to continue.',
              ),
            );
            break;
          case HTTP_STATUS.FORBIDDEN:
            ToastService.error(
              ToastService.t(
                'toast.restClient.httpForbidden',
                'You don’t have permission to do this. Please contact your care team.',
              ),
            );
            break;
          case HTTP_STATUS.NOT_FOUND:
            ToastService.error(
              ToastService.t(
                'toast.restClient.httpNotFound',
                'We couldn’t find this information. Please try again.',
              ),
            );
            break;
          case HTTP_STATUS.INTERNAL_SERVER_ERROR:
            ToastService.error(
              ToastService.t(
                'toast.restClient.httpInternalServerError',
                'We couldn’t complete this action. Please try again shortly.',
              ),
            );
            break;
          default:
            ToastService.error(
              ToastService.t(
                'toast.restClient.httpGeneric',
                'We couldn’t complete this action. Please try again.',
              ),
            );
        }
      }
    } else if (err.request) {
      if (err.code === 'ECONNABORTED') {
        ToastService.error(
          ToastService.t(
            'toast.restClient.networkTimeout',
            'This is taking longer than expected. Please try again.',
          ),
        );
      } else {
        ToastService.error(
          ToastService.t(
            'toast.restClient.networkError',
            'We couldn’t connect. Please check your internet connection and try again.',
          ),
        );
      }
    } else {
      ToastService.exception(err);
    }
  }

  buildUrl(endpoint) {
    const cleanEndpoint = endpoint ? endpoint.replace(/^\/+/, '') : '';
    const base = `api/${this.#version}/app${this.#servicePath}`;
    return cleanEndpoint ? `${base}/${cleanEndpoint}` : base;
  }

  async request(method, url, data, config = {}) {
    // Capture response ownership before any asynchronous request preparation.
    const app = useAppStore();
    const auth = useAuthStore();
    const requestToken = auth.token;
    const { silent = false, noLoading = false, sessionIndependent = false, ...restConfig } = config;
    // Pinned push operations must finish so their queued logout can unsubscribe.
    const authStateVersion = sessionIndependent ? null : auth.authStateVersion;

    try {
      if (!noLoading) {
        app.increaseLoadingCount();
      }

      // Keep ownership metadata local to Axios; it is not a header or API field.
      const axiosConfig = {
        ...restConfig,
        authStateVersion,
        url: this.buildUrl(url),
        method: method.toUpperCase(),
        data,
      };

      if (typeof FormData !== 'undefined' && data instanceof FormData) {
        axiosConfig.headers = {
          ...restConfig.headers,
          'Content-Type': undefined,
        };
      }

      const response = await this.#client.request(axiosConfig);

      // Requests belong to the auth state that sent them. A late /me or 401
      // must not replace or sign out a newer account/environment.
      if (authStateVersion !== null && authStateVersion !== auth.authStateVersion) {
        throw new Axios.CanceledError();
      }
      const result = response.data;

      if (result.code !== APP_CODES.SUCCESS) {
        // A pinned operation's rejected credentials do not revoke the current patient.
        if (authStateVersion !== null) {
          await this.handleApplicationError(result, {
            silent,
            publicAuth: restConfig.publicAuth,
            requestToken,
          });
        }
        throw new ApplicationError(result);
      }

      return result;
    } catch (error) {
      // Ignore failures from a retired session before toasting or signing anyone out.
      const hasRetiredSession =
        authStateVersion !== null && authStateVersion !== auth.authStateVersion;
      if (hasRetiredSession || Axios.isCancel(error)) {
        throw new Axios.CanceledError();
      }

      if (!silent) {
        this.handleHttpError(error);
      }

      // Keep application codes and retry metadata even when HTTP itself rejected the request.
      if (Axios.isAxiosError(error) && Number.isInteger(error.response?.data?.code)) {
        throw new ApplicationError(error.response.data);
      }

      throw error;
    } finally {
      if (!noLoading) {
        setTimeout(() => app.decreaseLoadingCount(), 500);
      }
    }
  }

  async get(url, params, config = {}) {
    const axiosConfig = mergeAxiosConfig(params, config);
    return this.request('GET', url, null, axiosConfig);
  }

  async post(url, data, config = {}) {
    return this.request('POST', url, data, config);
  }

  async patch(url, data, config = {}) {
    return this.request('PATCH', url, data, config);
  }

  async delete(url, data, config = {}) {
    return this.request('DELETE', url, data, config);
  }
}
